import {inject, singleton} from "tsyringe";
import {CommandHandler} from "../../decorators/command-handler.decorator";
import {Entity} from "../../domain/entities/entity";
import {MessageBroker} from "../../message-broker/message-broker";
import {EntityChangedPayload} from "../../message-broker/payloads/entity-changed.payload";
import {EntityCreatedPayload} from "../../message-broker/payloads/entity-created.payload";
import {EntityDeletedPayload} from "../../message-broker/payloads/entity-deleted.payload";
import {CommandBus} from "../../services/command-bus.service";
import {ClassOf} from "../../types/class-of.type";
import {BulkCommand} from "../bulk.command";
import {entityOfCommand} from "../carries-entity.interface";
import {ICommand} from "../i-command.interface";
import {ICommandHandler} from "./i-command.handler";

/**
 * Handles BulkCommand: sends the wrapped commands to the backend as a single
 * atomic request, then publishes a local entity.* event for every inner command.
 *
 * STOPGAP: the CommandBus only invokes the handler of a directly dispatched
 * command. Commands wrapped in a BulkCommand are forwarded to the backend
 * verbatim, so their individual handlers (e.g. RecolorIssueStatusCommandHandler)
 * never run and their entity.changed publish is skipped. This handler restores
 * that notification at the bulk level. To be replaced by backend-emitted domain
 * events over WebSocket (see .claude/refactoring-plans/event-driven-system.md).
 */
// BulkCommand is generic (BulkCommand<T extends Entity<T>>); the decorator only
// needs the constructor reference as a registration key, so the cast is safe.
@CommandHandler(BulkCommand as ClassOf<ICommand<ICommand[]>>)
@singleton()
export class BulkCommandHandler implements ICommandHandler<ICommand[]> {

    constructor(
        @inject(CommandBus) private readonly _commandBus: CommandBus,
        @inject(MessageBroker) private readonly _messageBroker: MessageBroker,
    ) {
    }

    /**
     * Sends the bulk command to the backend and, on success, publishes a local
     * entity change event for every inner command wrapping an Entity.
     */
    public async handle(command: ICommand<ICommand[]>): Promise<void> {
        if (!(await this._commandBus.addSendCommand(command))) {
            return;
        }

        for (const innerCommand of command.payload) {
            this._publishEntityEvent(innerCommand);
        }
    }

    /**
     * Publishes the matching entity.* event for a single inner command and marks
     * its entity as persisted. Skips inner commands whose payload is not an Entity.
     * The event type is derived from the command name verb (create / delete / *).
     */
    private _publishEntityEvent(innerCommand: ICommand): void {
        // A command with a CommandPayload carries its entity beside it (`CarriesEntity`, feature plan 0074: the
        // Recolor commands); without this they would publish nothing and stay unpersisted locally.
        const entity = entityOfCommand(innerCommand);
        if (entity === null) {
            return;
        }

        const prototype = entity.constructor as ClassOf<Entity>;
        const verb = innerCommand.name.split(`.`)[0];

        if (verb === `create`) {
            this._messageBroker.publish<EntityCreatedPayload<Entity>>({
                type: `entity.created`,
                identityHash: entity.uid,
                payload: {uid: entity.uid, prototype, entity}
            });
        } else if (verb === `delete`) {
            this._messageBroker.publish<EntityDeletedPayload<Entity>>({
                type: `entity.deleted`,
                identityHash: entity.uid,
                payload: {entity}
            });
        } else {
            this._messageBroker.publish<EntityChangedPayload<Entity>>({
                type: `entity.changed`,
                identityHash: entity.uid,
                payload: {uid: entity.uid, prototype, changeSet: entity.changeSet}
            });
        }

        entity.markAsPersisted();
    }
}
